import { createContext, useContext } from 'react';
import { TableCell, TableRow, Checkbox, Box } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { ColumnConfig } from '@/components/table/Table.tsx';

const RowReorderRowIndexContext = createContext<number | null>(null);

export function useRowReorderDragHandleProps(): {
  draggable: boolean;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: (event: React.DragEvent<HTMLElement>) => void;
} | null {
  const rowIndex = useContext(RowReorderRowIndexContext);
  if (rowIndex === null) return null;

  return {
    draggable: true,
    onDragStart: (event) => {
      event.dataTransfer.setData('text/plain', String(rowIndex));
      event.dataTransfer.effectAllowed = 'move';
      (event.currentTarget as HTMLElement).style.opacity = '0.5';
    },
    onDragEnd: (event) => {
      (event.currentTarget as HTMLElement).style.opacity = '';
    },
  };
}

interface TableRowProps {
  rowData: Record<string, any>;
  columns: ColumnConfig[];
  renderActions?: (rowData: Record<string, any>) => React.ReactNode;
  /** Dónde va la celda de acciones. Por defecto al final. */
  actionsPosition?: 'start' | 'end';
  rowIndex: number;
  isGhost?: boolean;
  selectable?: boolean;
  isRowSelectable?: boolean;
  rowKey?: string;
  selectedKeys?: Set<string>;
  maxSelectable?: number;
  onSelectRow?: (key: string, checked: boolean) => void;
  getRowStyle?: (rowData: Record<string, any>) => React.CSSProperties;
  onRowReorder?: (fromIndex: number, toIndex: number) => void;
}

export const TableRowComp = ({
  rowData,
  columns,
  renderActions,
  actionsPosition = 'end',
  rowIndex,
  isGhost,
  selectable,
  isRowSelectable = true,
  rowKey = '',
  selectedKeys,
  maxSelectable,
  onSelectRow,
  getRowStyle,
  onRowReorder,
}: TableRowProps) => {
  const isSelected = selectedKeys ? selectedKeys.has(rowKey) : false;
  const canSelect = selectable && isRowSelectable;
  const isDisabled =
    canSelect &&
    !isSelected &&
    selectedKeys &&
    maxSelectable !== undefined &&
    selectedKeys.size >= maxSelectable;

  const rowStyle = getRowStyle ? getRowStyle(rowData) : {};

  const handleDragOver = (event: React.DragEvent) => {
    if (!onRowReorder) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!onRowReorder) return;
    event.preventDefault();
    const fromIndex = parseInt(event.dataTransfer.getData('text/plain'), 10);
    if (!Number.isNaN(fromIndex) && fromIndex !== rowIndex) {
      onRowReorder(fromIndex, rowIndex);
    }
  };

  const reorderActive = Boolean(onRowReorder);

  const tableRow = (
    <TableRow
      key={rowIndex}
      draggable={false}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        backgroundColor:
          rowIndex % 2 === 0 ? 'var(--light-gray)' : 'var(--white-color)',
        opacity: isGhost ? 0.5 : 1,
        transition: 'opacity 0.3s ease',
        ...rowStyle,
      }}
    >
      {selectable && (
        <TableCell padding='checkbox' sx={{ width: '48px' }}>
          {canSelect ? (
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelectRow?.(rowKey, e.target.checked)}
              disabled={isDisabled}
            />
          ) : (
            <Box
              sx={{
                width: 42,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LockIcon
                fontSize='small'
                sx={{ color: 'action.disabled' }}
                titleAccess='No se puede rutear'
              />
            </Box>
          )}
        </TableCell>
      )}
      {renderActions && actionsPosition === 'start' && (
        <TableCell style={{ textAlign: 'left', width: '56px' }}>
          {renderActions(rowData)}
        </TableCell>
      )}
      {columns.map((column) => (
        <TableCell
          key={column.field}
          sx={
            reorderActive
              ? {
                  userSelect: 'text',
                  cursor: 'text',
                  fontSize: '1rem',
                  lineHeight: 1.45,
                }
              : {
                  fontSize: '1rem',
                  lineHeight: 1.45,
                }
          }
          style={{
            color: isGhost
              ? 'var(--secondary-color)'
              : column.field === 'id'
                ? 'var(--primary-color)'
                : 'var(--secondary-color)',
            fontStyle: isGhost ? 'italic' : 'normal',
          }}
        >
          {column.render
            ? column.render(rowData[column.field], rowData)
            : rowData[column.field]}
        </TableCell>
      ))}
      {renderActions && actionsPosition === 'end' && (
        <TableCell style={{ textAlign: 'right' }}>
          {renderActions(rowData)}
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <RowReorderRowIndexContext.Provider value={reorderActive ? rowIndex : null}>
      {tableRow}
    </RowReorderRowIndexContext.Provider>
  );
};
